import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { recipeCopy } from 'js/src/agent/skills/recipeCopy';
import { TOOL_NAMES } from 'js/src/agent/toolNames';
import type { Skill } from '../types';

export const createPodcastWaveformVideoSkill: Skill = {
    name: SKILL_NAMES.createPodcastWaveformVideo,
    description: 'Uploaded voiceover/podcast recording turned into an audiogram/waveform video',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        ...recipeCopy(SKILL_NAMES.createPodcastWaveformVideo),
        preview: 'audiogram',
        frames: ['audiogram-1', 'audiogram-2', 'audiogram-3', 'audiogram-4', 'audiogram-5'],
    },
    get instructions(): string {
        return `# Create Podcast Waveform Video (Audiogram)

For turning an uploaded recording — a podcast segment, interview clip, or narration —
into an audiogram: a video where the audio is the star, with an animated waveform
pulsing to the voice and animated captions. The Headliner style.

## Setup and questionnaire
- Following ${SKILL_NAMES.gatherRequirements}, ask everything still open in ONE
  ${TOOL_NAMES.askUserQuestion} call. Skip any question the request already answers.
  The questions:
  - **Aspect ratio** (type "aspect_ratio"). Designs are laid out per aspect ratio,
    so pass the answer to ${TOOL_NAMES.createBlankVideo} at creation — applying it
    afterwards reworks the whole video.
  - **Length / trim.** The finished video cannot be longer than the workspace's
    video duration limit (your plan limitations list it; the system maximum is 10
    minutes). Compare the recording's length to that limit:
    - If the recording is LONGER than the limit, it MUST be trimmed to fit — a
      full-length build would fail to export. Do NOT offer to keep the whole
      recording; ask which section, topic, or quote to keep, and tell the user the
      kept part has to fit under the limit.
    - If the recording is within the limit, ask whether to use the whole recording
      or trim it to a shorter cut.
    Either way you CAN honour a trim (see "Add the recording" below).
  - **The on-screen title.** Ask for the episode title and the podcast name — or,
    when a single person is speaking, the speaker's name and job title. Ask only
    when the transcript doesn't already make these clear; never invent them.

## Add the recording (trim here, not later)
- Add the recording with ${TOOL_NAMES.addRecordedVoiceoverSceneGroup} — one scene group.
  Transcription and word timings happen automatically.
- **To make a shorter cut, pre-trim at this step**: pass the segment to keep as
  \`start_quote\` + \`end_quote\` (the first and last few words, copied exactly from
  the transcript), or omit them to keep the whole recording. This is the only clean
  way to trim — do NOT add the whole recording and then carve it up. Never use
  ${TOOL_NAMES.splitSceneGroup} or scene deletion to fake a trim; scenes can't be deleted
  from voice-driven groups.
- A recording longer than the workspace's video duration limit MUST be added with
  \`start_quote\` + \`end_quote\` that keep it under the limit — never add such a
  recording whole, or the video can't be exported.
- That tool only accepts audio: if the user attached a *video* (e.g. a video
  podcast), that's a talking-head build (${TOOL_NAMES.addTalkingHeadSceneGroup}), not an
  audiogram, and this skill doesn't apply.

## The waveform: ONE overlay spanning the whole video
By definition this format is exactly one overlay — the waveform scene. Everything
else (title, captions) lives inside it; never add a second overlay.
1. Call ${TOOL_NAMES.findWaveformSceneDesign} to get the audiogram design's id. If it reports
   the template has no waveform design, tell the user this template doesn't support
   the audiogram style.
2. Call ${TOOL_NAMES.addVoiceoverScene} with a single \`scenes\` entry: that design id and
   \`cover_whole_group: true\`, so the audiogram scene spans the entire recording in one
   call. Don't enumerate the transcript token by token — cover_whole_group is the
   whole-recording shortcut, and it only works while the group is still just its auto
   base scene, which is exactly the case here.
3. Call ${TOOL_NAMES.setWaveformStyle} with the scene you just added — never the
   auto-generated base scene (its waveform is a static placeholder and can't be restyled) —
   and no waveform_name, always, right after placing the overlay. The picker IS the
   question: don't ask permission first ("want to see the options?") and don't use
   ${TOOL_NAMES.askUserQuestion} for it. Only pass waveform_name when the user has already named a style.

The audiogram must cover the ENTIRE recording (the trimmed recording, if you trimmed
it). Anything short leaves the surrounding silence — including the video's very first
frame and thumbnail — showing the template's plain default waveform base, a static bar
graphic that doesn't react to the audio. \`cover_whole_group: true\` owns everything from
frame 0 to the end; use it rather than a hand-built word range.

## The title: the design's own static text field
The waveform design may include a static (non-caption) text field for the title.
Use it — don't add a separate intro overlay for the title.
- After ${TOOL_NAMES.setWaveformStyle}, read the video with ${TOOL_NAMES.getVideoState} and
  look at the waveform scene for a text asset (e.g. \`text_1\`) that is NOT a caption.
- If one exists, fill it with ${TOOL_NAMES.setText} (the scene's \`scene_id\` and that
  \`asset_name\`): the episode title and podcast name, or the speaker's name and job
  title. Keep it short.
- If the design has no such field, skip it — the audio and captions already carry the
  video. Do NOT add an extra overlay to make up for a missing field.

## Captions
The audiogram design includes animated captions synced to the speech — you get them
for free. Do NOT also enable scene-group captions with ${TOOL_NAMES.setCaptions}; that would
double up the text. Do NOT write the title into a caption asset.

## Defaults
- Skip background music unless the user explicitly asks — music under spoken
  podcast audio muddies it.

## Finishing checks
On top of the standard review, ${TOOL_NAMES.imageAsk} the waveform scene: the waveform is
clearly visible, the captions are readable and don't collide with the waveform or the
title, any title text is correct, and the FIRST frame of the video shows the audiogram
design — not a plain static waveform placeholder.`;
    },
};
